import {
  generators,
  PostedEntityRequest,
  PullRequestRequest,
  RefreshRequest,
} from './generators';
import { call, put, all } from 'redux-saga/effects';
import { APIResult } from 'utils/apidata';
import { PostedEntity, PullRequest, RefreshPullRequest } from './models';
import {
  postedCommentSnippetsPages,
  postedCommentsPages,
  postedReviewsPages,
} from 'stores/pullRequests/selectors';

type PostedEntityType =
  | 'postedComments'
  | 'postedCommentSnippets'
  | 'postedReviews';

export const postedEntitiesPagesInitialURL = ({
  request,
  entityType,
}: {
  request: PostedEntityRequest<PostedEntity>;
  entityType: PostedEntityType;
}) => {
  const { providerId, organizationSlug, projectName, reviewNumber } = request;

  const baseUrl = `/api/v1/repositories/${encodeURIComponent(
    providerId
  )}/${encodeURIComponent(organizationSlug)}/${encodeURIComponent(
    projectName
  )}/pull-requests/${encodeURIComponent(reviewNumber)}`;

  switch (entityType) {
    case 'postedReviews':
      return `${baseUrl}/posted-reviews`;
    case 'postedCommentSnippets':
      return `${baseUrl}/posted-comment-snippets`;
    case 'postedComments':
    default:
      return `${baseUrl}/posted-comments`;
  }
};

export const postedComments = generators.postedComments.saga(
  (request) =>
    postedEntitiesPagesInitialURL({ request, entityType: 'postedComments' }),
  postedCommentsPages
);

export const postedCommentSnippets = generators.postedCommentSnippets.saga(
  (request) =>
    postedEntitiesPagesInitialURL({
      request,
      entityType: 'postedCommentSnippets',
    }),
  postedCommentSnippetsPages
);

export const postedReviews = generators.postedReviews.saga(
  (request) =>
    postedEntitiesPagesInitialURL({ request, entityType: 'postedReviews' }),
  postedReviewsPages
);

export function* updatePullRequest(
  request: PullRequestRequest,
  response: APIResult<PullRequest>
) {
  yield put(generators.entries.success(request, response));
}

export const toggleReview = generators.toggleReview.saga(
  (api, { uuid, enabled, h1ReportID }) =>
    call(api.post, `/api/v1/pull-requests/${uuid}/toggle-review`, {
      enabled,
      h1_report_id: h1ReportID ?? null,
    }),
  updatePullRequest
);

export function* handleRefresh(
  request: RefreshRequest,
  response: APIResult<RefreshPullRequest>
) {
  if (!response.success) {
    return;
  }
  yield all(
    response.data.data.map((pullRequest: PullRequest) =>
      put(
        generators.entries.success(
          { uuid: request.uuid },
          {
            url: '',
            status: 200,
            success: true,
            data: pullRequest,
          }
        )
      )
    )
  );
}

export const refresh = generators.refresh.saga(
  (api, { uuid, projectUUID }) =>
    call(api.get, `/api/v1/projects/${projectUUID}/pull-requests?uuid=${uuid}`),
  handleRefresh
);

export function* sagas() {
  yield postedComments;
  yield postedCommentSnippets;
  yield postedReviews;
  yield toggleReview;
  yield refresh;
}
